// Responsável (vendedor) do negócio na aba Editar — regra pura, testável sem antd.
//
// Por que existe (14/09/2026): a aba Editar achava o vendedor comparando o NOME gravado
// em comercial.propostas.responsavel com o nome atual do Cognito e enviava
// `vendedor_email: match?.email || null`. Quando a grafia não batia byte a byte
// ("Gustavo Araújo" × "Gustavo Schefelbanis Araujo"), o primeiro "Salvar" apagava o
// vendedor_email sem ninguém tocar no campo. Esse e-mail decide quem recebe o sino do
// aceite digital, o responsável sugerido ao iniciar o contrato (e os e-mails do kick-off)
// e o vendedor da assinatura da proposta.
//
// Regra:
//   - O Select trabalha pelo E-MAIL (chave do perfil), não pelo nome.
//   - Campo não alterado → não envia responsavel nem vendedor_email: o gravado fica.
//   - Outro vendedor escolhido → grava nome e e-mail daquele vendedor.
//   - Campo limpo → grava os dois vazios.
//   - O valor gravado que não está na lista aparece como opção própria, em vez de sumir.

export interface VendedorLista {
  email: string;
  nome: string;
}

export interface ResponsavelGravado {
  responsavel?: string | null;
  vendedor_email?: string | null;
}

export interface OpcaoResponsavel {
  value: string;
  label: string;
}

export interface CamposResponsavel {
  responsavel?: string | null;
  vendedor_email?: string | null;
}

// Nome gravado sem e-mail (grafia antiga). Não colide com e-mail: e-mail não tem espaço
// nem começa com isso.
const PREFIXO_SEM_EMAIL = 'sem-email:';

function texto(s: string | null | undefined): string {
  return (s ?? '').trim();
}

function chaveEmail(s: string | null | undefined): string {
  return texto(s).toLowerCase();
}

/** Valor com que o Select abre para este negócio. */
export function valorInicialResponsavel(neg: ResponsavelGravado): string | undefined {
  const email = chaveEmail(neg.vendedor_email);
  if (email) return email;
  const nome = texto(neg.responsavel);
  return nome ? PREFIXO_SEM_EMAIL + nome : undefined;
}

/** Opções do Select: a lista de vendedores + o valor gravado, se ele não estiver nela. */
export function opcoesResponsavel(
  neg: ResponsavelGravado,
  vendedores: VendedorLista[],
): OpcaoResponsavel[] {
  const opcoes: OpcaoResponsavel[] = [];
  const vistos = new Set<string>();
  for (const v of vendedores) {
    const value = chaveEmail(v.email);
    if (!value || vistos.has(value)) continue;
    vistos.add(value);
    opcoes.push({ value, label: texto(v.nome) || value });
  }

  const inicial = valorInicialResponsavel(neg);
  if (inicial && !vistos.has(inicial)) {
    const nome = texto(neg.responsavel);
    let label: string;
    if (inicial.startsWith(PREFIXO_SEM_EMAIL)) {
      label = `${nome} (sem vendedor vinculado)`;
    } else if (vendedores.length === 0) {
      // Lista ainda carregando: não acusa "fora da lista" antes da hora.
      label = nome || inicial;
    } else {
      label = `${nome || inicial} (fora da lista de vendedores)`;
    }
    opcoes.unshift({ value: inicial, label });
  }
  return opcoes;
}

/** Aviso curto embaixo do campo, quando o valor escolhido merece explicação. */
export function avisoResponsavel(
  vendedores: VendedorLista[],
  valor: string | null | undefined,
): string | undefined {
  const v = texto(valor);
  if (!v) return undefined;
  if (v.startsWith(PREFIXO_SEM_EMAIL)) {
    return 'Nome antigo sem vendedor vinculado: ninguém recebe o aviso do aceite e o contrato não '
      + 'sugere responsável. Para vincular, escolha a pessoa na lista e salve.';
  }
  if (vendedores.length > 0 && !vendedores.some(x => chaveEmail(x.email) === v)) {
    return 'Este vendedor não está mais na lista (usuário desativado ou fora do grupo comercial). '
      + 'O vínculo gravado continua enquanto ninguém trocar.';
  }
  return undefined;
}

/**
 * O que enviar no PUT /comercial/propostas/:id.
 * Objeto vazio = não mexe no responsável nem no vendedor_email gravados.
 */
export function camposResponsavelAoSalvar(
  neg: ResponsavelGravado,
  vendedores: VendedorLista[],
  selecionado: string | null | undefined,
): CamposResponsavel {
  const valor = texto(selecionado);
  const inicial = valorInicialResponsavel(neg) ?? '';

  if (valor === inicial) return {};
  if (!valor) return { responsavel: null, vendedor_email: null };

  const vendedor = vendedores.find(x => chaveEmail(x.email) === valor);
  // Valor que não é da lista nem o gravado: não inventa, preserva o que está no banco.
  if (!vendedor) return {};

  // Nome e e-mail CRUS da lista, como gravam o FormNovoNegocio e o Dwight.
  return {
    responsavel: vendedor.nome || vendedor.email,
    vendedor_email: vendedor.email,
  };
}
